import axios, { type CancelToken, type CancelTokenSource } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ASYNC_STATUS } from '../constants'
import type {
  AsyncResponse,
  CancellableAsyncRequestArgs,
  CancellableAsyncRequestReturnType,
  FailedAsyncResponse,
  SuccessAsyncResponse,
} from '../types/request'
import useCallbackRef from './useCallbackRef'

const useCancelableRequest = <
  F extends (token: CancelToken, ...rest: any[]) => Promise<any>,
>(
  request: F
): readonly [
  AsyncResponse<CancellableAsyncRequestReturnType<F>>,
  (
    ...args: CancellableAsyncRequestArgs<F>
  ) => Promise<
    | SuccessAsyncResponse<CancellableAsyncRequestReturnType<F>>
    | FailedAsyncResponse
  >,
] => {
  const requestRef = useCallbackRef(request)
  const cancelToken = useRef<CancelTokenSource>(axios.CancelToken.source())
  const [response, setResponse] = useState<
    AsyncResponse<CancellableAsyncRequestReturnType<F>>
  >({
    asyncStatus: ASYNC_STATUS.IDLE,
    data: undefined,
  })

  const execute = useCallback(
    async (...args: CancellableAsyncRequestArgs<F>) => {
      cancelToken.current.cancel()
      cancelToken.current = axios.CancelToken.source()
      setResponse({ asyncStatus: ASYNC_STATUS.PENDING, data: undefined })
      try {
        const data = await requestRef.current(
          cancelToken.current.token,
          ...args
        )
        setResponse({ asyncStatus: ASYNC_STATUS.SUCCEEDED, data })

        return { asyncStatus: ASYNC_STATUS.SUCCEEDED, data } as const
      } catch (error) {
        if (!axios.isCancel(error)) {
          setResponse({
            asyncStatus: ASYNC_STATUS.FAILED,
            error,
            data: undefined,
          })
        }

        return {
          asyncStatus: ASYNC_STATUS.FAILED,
          error,
          data: undefined,
        } as const
      }
    },
    []
  )

  useEffect(() => () => cancelToken.current.cancel(), [])

  return [response, execute] as const
}

export default useCancelableRequest
